import i18n from '@/i18n'
import { extract } from '@/tools/functions'
import { DialogType } from '@/constants/constants'
import { filterRulesMapped } from '@/tools/filterRules'
import { mapActions } from 'vuex'
import { FilterType, DataType, CellType } from '@colven/common-domain-lib/lib'

const COMMAND_START_SYMBOL = '>'
const COMMAND_END_SYMBOL = '<'

export default {
    name: 'CommandsFileInputComponent',
    components: {
    },
    model: {
        prop: 'model',
        event: 'change'
    },
    props: {
        model: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        // reglas de validación
        validationRules: {
            required: (value) => !!value || `${i18n.t('required')}`,
            requiredWithCheck: (check, value) => !check || !!value || `${i18n.t('required')}`,
            moreThan: (value, minValue) => value == null || minValue == null || Number(value) > Number(minValue) || `${i18n.t('error.moreThan', {value: minValue})}`,
            greaterThanEqual: (value, minValue) => value == null || minValue ==null || Number(value) >= Number(minValue) || `${i18n.t('error.greaterThanEqual', {value: minValue})}`,
            lessThanEqual: (value, maxValue) => value == null || maxValue ==null || Number(value) <= Number(maxValue) || `${i18n.t('error.lessThanEqual', {value: maxValue})}`            
        },
        // model para el formulario
        validForm: false,
        // archivo seleccionado
        fileSelected: null,
        // dialog con la vista previa de comandos
        showPreview: false,
        previewCommandsHeaders: [],
        previewCommandsFilterRules: {},
        previewCommands: [],
        previewFilterRules: {
            name: (str1, str2) => {
                if (!str2) {
                    return true
                } else if (str1) {
                    return (str1.toString().toLowerCase()).includes(str2.toString().toLowerCase())
                }
            }
        },
        previewLoading: false,
        // para activar el tooltip de vista previa unos segundos luego de elegir un archivo
        previewTooltipActivator: false,
        previewTooltipActivatorInstance: undefined,
        // para activar el tooltip del template
        templateTooltipActivator: false,
        templateTooltipActivatorInstance: undefined,
        // para abrir la consola de comandos
        showCommandsConsole: false,
        // modo fullscreen de la consola de comandos
        fullscreenCommandsConsole: false,
        // modo oscuro de la consola
        darkCommandsConsole: true,
        // botones del componente tabla de la vista previa de comandos
        previewCommandsTableComponentButtons: [],
        // botones de cada fila de la tabla de vista previa de comandos
        rowActions: [
            // eliminar
            {
                id: 1,
                name: i18n.t('delete'),
                color: 'red',
                icon: 'fa-trash-alt'
            }
        ],
        // comandos subidos
        commands: []
    }),
    computed: {
    },
    created () {
    },
    mounted () {
        // tooltip del archivo template
        this.templateTooltipActivator = true
        this.templateTooltipActivatorInstance = setTimeout(() => {
            this.templateTooltipActivator = false
        }, 1500)

        // Botones del TableComponent
        this.previewCommandsTableComponentButtons.push(
            {
                id: 'masive-delete',
                tooltip: i18n.t('multipleDelete'),
                icon: 'delete_outline',
                action: this.multipleDelete.bind(this),
                selectedData: true
            })
    },
    beforeDestroy() {
        clearInterval(this.previewTooltipActivatorInstance)
        this.previewTooltipActivatorInstance = undefined
        clearInterval(this.templateTooltipActivatorInstance)
        this.templateTooltipActivatorInstance = undefined
    },
    methods: {
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * Resetea los campos del formulario
         */
        resetFields() {
            if (this.$refs.customInputComponent) {
                this.$refs.customInputComponent.reset()
            }
        },
        /**
         * Cuando se sube un nuevo archivo en el file-input
         * Procesa los comandos que tiene el archivo
         * @param {*} file
         */
        onFileSelected(file) {
            if (file) {
                this.model.file = file

                // FileReader para leer el contenido del archivo y asignarlo a 'data' del modelo
                const fileReader = new FileReader()
                fileReader.readAsText(file)

                fileReader.onload = (event) => {
                    this.getCommands(event.target.result)
                }

                fileReader.onerror = () => {
                    console.error('Error al leer el archivo subido')
                }
            } else {
                this.model.file = null
            }
        },
        /**
         * Procesa el texto subido en el archivo
         * @param {*} text
         * @param {*} loadedFromFile
         */
        getCommands (text, loadedFromFile = true) {
            const stringExtractor = extract([COMMAND_START_SYMBOL,COMMAND_END_SYMBOL])
            const commandsUploaded = stringExtractor(text)
            this.model.data.push(...commandsUploaded)

            // activo el tooltip de vista previa
            this.previewTooltipActivator = true
            this.previewTooltipActivatorInstance = setTimeout(() => {
                this.previewTooltipActivator = false
            }, 1500)

            // agrego la procedencia del comando
            const commandsUploadedWithProcedence = commandsUploaded.map(command => ({ command, loadedFromFile }))
            this.commands.push(...commandsUploadedWithProcedence)

            // actualizo los datos de la vista previa
            this.updatePreviewData()
        },
        /**
         * Datos para la tabla de vista previa
         */
        updatePreviewData () {
            this.previewLoading = true
            this.previewCommands = []
            const pushPreviewData = this.commands.map((element, index) => ({
                id: this.previewCommands.length + index,
                name: `>${element.command}<`,
                loadedFromFile: {
                    value: element.loadedFromFile,
                    text: element.loadedFromFile ? this.$t('yes') : this.$t('no')
                },
                loadedFromFileIcon: element.loadedFromFile ? 'mdi-check' : 'mdi-close',
                loadedFromFileColor: element.loadedFromFile ? 'green' : 'red'
            }))
            this.previewCommands.push(...pushPreviewData)
            this.previewLoading = false
        },
        /**
         * Muestra el dialog de vista previa
         */
        showPreviewDialog () {
            this.showPreview = true
            this.previewLoading = true

            // headers
            this.previewCommandsHeaders.cleanAndUpdate([
                {
                    text: this.$t('commandMassiveUpload.command'),
                    filterType: FilterType.textField,
                    selected: true,
                    sortable: true,
                    type: DataType.string,
                    spinner: false,
                    value: 'name'
                },                
                {
                    text: this.$t('commandMassiveUpload.loadedFromFile'),
                    filterType: FilterType.multiSelect,
                    options: [
                        this.$t('no'),
                        this.$t('yes'),
                    ],
                    selected: true,
                    sortable: true,
                    type: DataType.boolean,
                    spinner: false,
                    cellType: CellType.iconText,
                    cellConfig: {
                        icon: 'loadedFromFileIcon',
                        color: 'loadedFromFileColor',
                    },
                    value: 'loadedFromFile'
                }
            ])
            this.setTableFilterRules()

            this.previewLoading = false
        },
        /**
         * Descarga un archivo 'template'
         */
        downloadTemplate () {
            let text = this.$t('commandMassiveUpload.template.description')
            for (let n = 1; n <= 15; n++) {
                text += `\n${this.$t('commandMassiveUpload.template.command', { n })}`
            }
            const filename = 'template.txt'
            this.generateTextFile(text, filename)
        },
        /**
         * Genera un archivo .txt
         * @param {*} text
         * @param {*} filename
         */
        generateTextFile (text, filename) {
            const a = document.createElement('a')
            document.body.appendChild(a)
            a.style = 'display: none'
            const blob = new Blob([text], {type: 'text/plain'})
            const url = window.URL.createObjectURL(blob)
            a.href = url
            a.download = filename
            a.click()
            window.URL.revokeObjectURL(url)
        },
        /**
         * Abre la consola de comandos
         */
        openCommandConsole () {
            this.showCommandsConsole = true
        },
        /**
         * Cancelar/cerrar la consola de comandos
         */
        commandsConsoleCancel () {
            this.showCommandsConsole = false
            this.fullscreenCommandsConsole = false
            this.darkCommandsConsole = true
            this.model.consoleInputCommands = null
        },
        /**
         * Guardar los comandos de la consola de comandos
         */
        commandsConsoleSave () {
            this.getCommands(this.model.consoleInputCommands, false)
            this.commandsConsoleCancel()
        },
        /**
         * Eliminación múltiple de comandos de la tabla de vista previa
         * @param {*} data
         */
        multipleDelete(data) {
            this.openDialog({
                title: this.$t('commandMassiveUpload.multipleDeleteTitle'),
                text: this.$t('commandMassiveUpload.multipleDeleteText'),
                type: DialogType.QUESTION,
                overlayOpacity: 0.5,
                actionButtons: true,
                yesAction: () => {
                    const ids = data.map(e => e.id)
                    this.deleteCommands(ids)
                },
                noAction: () => {
                }
            })
        },
        /**
         * Elimina comandos a partir del índice en el arreglo de comandos
         * @param {*} indexs
         */
        deleteCommands(indexs) {
            const filteredCommands = this.commands.filter((element, index) => !indexs.includes(index))
            this.commands.cleanAndUpdate(filteredCommands)
            this.model.data.cleanAndUpdate(this.commands.map(element => (element.command)))

            // actualizo los datos de la tabla de vista previa
            this.updatePreviewData()
        },
        /**
         * Handler de acciones de las filas
         * @param {*} button 
         * @param {*} data 
         */
        rowButtonClicked(button, data) {
            switch (button.id) {
                case 1:
                    this.openDialog({
                        title: this.$t('commandMassiveUpload.deleteTitle'),
                        text: this.$t('commandMassiveUpload.deleteText', {name: `${data.name}`}),
                        type: DialogType.QUESTION,
                        overlayOpacity: 0.5,
                        actionButtons: true,
                        yesAction: () => {
                            this.deleteCommands([data.id])
                        },
                        noAction: () => {
                        }
                    })
                    break
                default:
                    console.error('Botón no programado en el componente')
            }
        },
        /**
         * Crea las reglas de filtrado para la tabla de vista previa de comandos
         */
        setTableFilterRules() {
            this.previewCommandsFilterRules = {}
            this.previewCommandsHeaders.forEach(header => {
                this.previewCommandsFilterRules[header.value] = filterRulesMapped[header.filterType]
            })
        },
        /**
         * Ícono para mostrar/ocultar el formulario de parámetros de reenvío
         * @param {*} show
         */
        arrowIcon (show) {
            if (show) {
                return 'arrow_drop_up'
            } else {
                return 'arrow_drop_down'
            }
        }
    }
}
